import React, { useState, useEffect } from 'react';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import Button from '../common/Button';
import PreferencesService from '../../services/preferencesService';
import ToastContainer from '../common/ToastContainer';
import { useToast } from '../../hooks/useToast';
import './Preferences.css';

const Preferences = () => {
  const { preferences, updatePreferences, isLoading: preferencesLoading, error, clearError } = useUserPreferences();
  const [formData, setFormData] = useState({
    dayViewStartTime: '08:00',
    dayViewEndTime: '18:00',
    treatmentPlanValidityDays: 30,
    calendarDotColor: '#3182ce'
  });
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const { toasts, removeToast, success: showSuccess, error: showError } = useToast();

  // Update form data when preferences are loaded
  useEffect(() => {
    if (!preferencesLoading) {
      setHasLoaded(true);
      setFormData({
        dayViewStartTime: preferences.dayViewStartTime,
        dayViewEndTime: preferences.dayViewEndTime,
        treatmentPlanValidityDays: preferences.treatmentPlanValidityDays || 30,
        calendarDotColor: preferences.calendarDotColor || '#3182ce'
      });
    }
  }, [preferences, preferencesLoading]);

  // Clear any store errors when component mounts or when clearing local message
  useEffect(() => {
    if (error) {
      showError(error);
      clearError();
    }
  }, [error, clearError, showError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const validateForm = () => {
    const startTime = formData.dayViewStartTime;
    const endTime = formData.dayViewEndTime;
    const validityDays = formData.treatmentPlanValidityDays;

    if (startTime >= endTime) {
      showError('La hora de inicio debe ser menor a la hora de fin');
      return false;
    }

    if (!validityDays || validityDays < 1 || validityDays > 365) {
      showError('Los días de validez deben estar entre 1 y 365');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Save preferences using the hook
      const isSaved = await updatePreferences(formData);

      if (isSaved) {
        showSuccess('Preferencias guardadas exitosamente');
      } else {
        showError('Error al guardar las preferencias. Inténtalo de nuevo.');
      }
    } catch (error) {
      console.error('Error saving preferences:', error);
      showError('Error al guardar las preferencias. Inténtalo de nuevo.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="preferences-container">
      <div className="preferences-header">
        <h1>Preferencias</h1>
        <p>Personaliza tu experiencia en la aplicación</p>
      </div>

      {preferencesLoading && !hasLoaded ? (
        <div className="preferences-loading">
          <div className="loading-spinner"></div>
        </div>
      ) : (
        <div className="preferences-content">
          <div className="preferences-card">
            <form onSubmit={handleSubmit} className="preferences-form">
              <div className="form-section">
                <h3>Vista de Calendario</h3>
                <p className="section-description">
                  Configura las horas de inicio y fin para la vista diaria del calendario de citas
                </p>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="dayViewStartTime">
                      <i className="fas fa-clock"></i>
                      Hora de Inicio
                    </label>
                    <input
                      type="time"
                      id="dayViewStartTime"
                      name="dayViewStartTime"
                      value={formData.dayViewStartTime}
                      onChange={handleInputChange}
                      min="00:00"
                      max="23:59"
                      required
                    />
                    <small className="field-help">
                      La primera hora visible en la vista diaria del calendario
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="dayViewEndTime">
                      <i className="fas fa-clock"></i>
                      Hora de Fin
                    </label>
                    <input
                      type="time"
                      id="dayViewEndTime"
                      name="dayViewEndTime"
                      value={formData.dayViewEndTime}
                      onChange={handleInputChange}
                      min="00:00"
                      max="23:59"
                      required
                    />
                    <small className="field-help">
                      La última hora visible en la vista diaria del calendario
                    </small>
                  </div>
                </div>

                <div className="form-row single">
                  <div className="form-group">
                    <label htmlFor="calendarDotColor">
                      <i className="fas fa-palette"></i>
                      Color de tu usuario en el calendario
                    </label>
                    <input
                      type="color"
                      id="calendarDotColor"
                      name="calendarDotColor"
                      value={formData.calendarDotColor}
                      onChange={handleInputChange}
                    />
                    <small className="field-help">
                      Se usa para identificarte en el calendario mensual
                    </small>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Planes de Tratamiento</h3>
                <p className="section-description">
                  Configura el tiempo de validez para las cotizaciones de planes de tratamiento
                </p>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="treatmentPlanValidityDays">
                      <i className="fas fa-calendar-check"></i>
                      Días de Validez para Cotizaciones
                    </label>
                    <input
                      type="number"
                      id="treatmentPlanValidityDays"
                      name="treatmentPlanValidityDays"
                      value={formData.treatmentPlanValidityDays}
                      onChange={handleInputChange}
                      min="1"
                      max="365"
                      required
                    />
                    <small className="field-help">
                      Cantidad de días que una cotización de plan de tratamiento permanece válida (1-365 días)
                    </small>
                  </div>

                  <div className="form-group">
                    <div className="validity-info">
                      <h4>Información de Validez</h4>
                      <div className="validity-details">
                        <i className="fas fa-info-circle"></i>
                        <span>Los planes de tratamiento creados tendrán una validez de {formData.treatmentPlanValidityDays} días desde su fecha de creación</span>
                      </div>
                      <div className="validity-example">
                        <i className="fas fa-calendar-alt"></i>
                        <span>Ejemplo: Un plan creado hoy expirará el {PreferencesService.formatDateWithUserTimezone(
                          new Date(Date.now() + formData.treatmentPlanValidityDays * 24 * 60 * 60 * 1000),
                          preferences,
                          { year: 'numeric', month: '2-digit', day: '2-digit' }
                        )}</span>
                      </div >
                    </div>
                  </div>
                  <ToastContainer toasts={toasts} removeToast={removeToast} />
                </div >
              </div>

              <div className="form-actions">
                <Button
                  type="submit"
                  variant="primary"
                  loading={isLoading}
                  icon="fas fa-save"
                >
                  {isLoading ? 'Guardando...' : 'Guardar Preferencias'}
                </Button>
              </div>
            </form>
          </div>

          <div className="preferences-info">
            <div className="info-card">
              <h3>
                <i className="fas fa-info-circle"></i>
                Información
              </h3>
              <div className="info-item">
                <h4>Vista de Calendario</h4>
                <p>
                  Las horas de inicio y fin determinan el rango visible en la vista diaria del calendario.
                  Las citas fuera de este rango seguirán siendo visibles pero requerirán desplazamiento.
                </p>
              </div>
              <div className="info-item">
                <h4>Validez de Planes de Tratamiento</h4>
                <p>
                  Los días de validez determinan cuánto tiempo permanece vigente una cotización de plan de tratamiento.
                  Después de este período, el plan se marcará como expirado y se mostrará un mensaje de alerta.
                </p>
              </div>
              <div className="info-item">
                <h4>Aplicación de Cambios</h4>
                <p>
                  Los cambios se aplicarán inmediatamente después de guardar y se mantendrán
                  para futuras sesiones.
                </p>
              </div>
            </div>
          </div>
        </div >
      )}
    </div >
  );
}; export default Preferences;
